import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, Send, Download, Link2, Copy, PlugZap, CheckCircle2,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, StatusPill } from "@/components/tac";
import { generateQuotePdf, quotePdfBase64 } from "@/lib/quotePdf";

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, workspace } = useAuth();
  const canOffice = ["owner", "dispatcher"].includes(user?.role);

  const [q, setQ] = useState(null);
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [pricebook, setPricebook] = useState([]);
  const [pick, setPick] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/quotes/${id}`);
    setQ(data); setItems(data.line_items || []); setTax(data.tax_rate || 0);
    setNotes(data.notes || ""); setSchedule(data.payment_schedule || []);
    setEmail(data.customer_email || "");
  };
  useEffect(() => {
    load().catch(() => toast.error("Quote not found"));
    api.get("/pricebook").then((r) => setPricebook(r.data)).catch(() => {});
  }, [id]);

  const subtotal = useMemo(() => items.reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0), [items]);
  const total = useMemo(() => subtotal * (1 + (tax || 0) / 100), [subtotal, tax]);
  const estCost = useMemo(() => items.reduce((s, it) => s + (it.qty || 0) * (it.unit_cost || 0), 0), [items]);
  const estMarginPct = subtotal > 0 && estCost > 0 ? Math.round((subtotal - estCost) / subtotal * 1000) / 10 : null;

  const setItem = (i, patch) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addFromPricebook = () => {
    const p = pricebook.find((x) => x.id === pick);
    if (!p) return;
    setItems((a) => [...a, { item: p.name, qty: 1, unit_price: p.unit_price, trade: p.trade, unit_cost: p.cost_price ?? null, vendor: p.vendor ?? null }]);
    setPick("");
  };
  const addBlank = () => setItems((a) => [...a, { item: "", qty: 1, unit_price: 0 }]);

  const addMilestone = () => setSchedule((s) => [...s, { label: "Milestone", mode: "percent", value: 25 }]);
  const setMilestone = (i, patch) => setSchedule((s) => s.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const delMilestone = (i) => setSchedule((s) => s.filter((_, idx) => idx !== i));
  const mAmount = (m) => (m.mode === "fixed" ? Number(m.value || 0) : total * Number(m.value || 0) / 100);

  const save = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/quotes/${id}`, {
        line_items: items, tax_rate: tax, notes, status,
        payment_schedule: schedule.map((m) => ({ label: m.label, mode: m.mode, value: Number(m.value) || 0, id: m.id, status: m.status })),
      });
      setQ(data); setSchedule(data.payment_schedule || []);
      toast.success("Quote saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const downloadPdf = () => generateQuotePdf({ ...q, line_items: items, tax_rate: tax, subtotal, tax: subtotal * tax / 100, total, notes, payment_schedule: schedule.map((m) => ({ ...m, amount: mAmount(m) })) }, workspace?.name);

  const sendQuote = async () => {
    setBusy(true);
    try {
      await save(); // persist latest before sending
      const fresh = (await api.get(`/quotes/${id}`)).data;
      const b64 = quotePdfBase64(fresh, workspace?.name);
      const { data } = await api.post(`/quotes/${id}/send`, { pdf_base64: b64, filename: `${fresh.number}.pdf`, email: email || null });
      await load();
      if (data.emailed) toast.success(`Quote emailed to ${data.email_to}`);
      else toast.success("Quote ready to share (email not sent - connect Gmail)");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const pushAccounting = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/push-accounting`);
      await load();
      const refs = [data.qbo_ref, data.xero_ref].filter(Boolean).join(", ");
      toast.success(refs ? `Pushed: ${refs}` : "Pushed to accounting");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const publicUrl = q?.public_token ? `${window.location.origin}/q/${q.public_token}` : null;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };

  if (!q) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="quote-detail">
      <button onClick={() => nav(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-white">{q.number}</h1>
        <StatusPill status={{ draft: "scheduled", sent: "en_route", accepted: "completed", declined: "none" }[q.status]} />
      </div>
      <p className="mt-1 text-sm text-slate-400">{q.customer_name || "No customer"}{q.summary ? ` · ${q.summary}` : ""}</p>

      {/* Line items */}
      <div className="mt-6 card-tac p-5">
        <div className="mb-3 font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Line items</div>
        {items.map((it, i) => (
          <div key={i} className="mb-2 flex items-center gap-2" data-testid={`ql-row-${i}`}>
            <input value={it.item} onChange={(e) => setItem(i, { item: e.target.value })} data-testid={`ql-item-${i}`}
              className="flex-1 rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-sm text-white" />
            <input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: parseFloat(e.target.value) || 0 })} data-testid={`ql-qty-${i}`}
              className="w-16 rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-right text-sm text-white" />
            <span className="text-slate-600 text-xs">×$</span>
            <input type="number" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: parseFloat(e.target.value) || 0 })} data-testid={`ql-price-${i}`}
              className="w-24 rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-right text-sm text-white" />
            <span className="w-20 text-right font-mono-tac text-sm text-slate-300">${((it.qty || 0) * (it.unit_price || 0)).toFixed(0)}</span>
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.some((it) => it.vendor || it.unit_cost) && (
          <p className="mt-1 font-mono-tac text-[10px] text-slate-600">Vendor & cost auto-attached from your pricebook for lines added below.</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="pricebook-picker"
            className="rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-sm text-white">
            <option value="">Add from pricebook…</option>
            {pricebook.map((p) => <option key={p.id} value={p.id}>{p.name} (${p.unit_price})</option>)}
          </select>
          <button onClick={addFromPricebook} disabled={!pick} data-testid="add-pricebook-btn" className="rounded-full border border-[#2E7BE4]/60 px-3 py-1.5 text-xs text-[#2E7BE4] disabled:opacity-40">Add item</button>
          <button onClick={addBlank} data-testid="add-blank-line-btn" className="inline-flex items-center gap-1 text-xs text-cyan-400"><Plus className="h-3 w-3" /> Blank line</button>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-800 pt-3 text-sm text-slate-400">
          Tax % <input type="number" value={tax} onChange={(e) => setTax(parseFloat(e.target.value) || 0)} data-testid="quote-tax-input"
            className="w-16 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-right text-white" />
        </div>
        <div className="mt-2 flex items-baseline justify-between">
          <span className="text-sm text-slate-400">Total</span>
          <span data-testid="quote-total" className="font-display text-2xl font-bold text-white">${total.toFixed(2)}</span>
        </div>
        {estCost > 0 && (
          <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-2 text-xs" data-testid="quote-margin-preview">
            <span className="text-slate-400">Est. cost <b className="text-slate-300">${estCost.toFixed(0)}</b> from pricebook vendors</span>
            <span className={`font-mono-tac font-bold ${estMarginPct === null ? "text-slate-400" : estMarginPct >= 40 ? "text-emerald-400" : estMarginPct >= 20 ? "text-amber-400" : "text-rose-400"}`} data-testid="quote-est-margin">
              Est. margin {estMarginPct === null ? " - " : `${estMarginPct}%`}
            </span>
          </div>
        )}
      </div>

      {/* Payment schedule */}
      <div className="mt-6 card-tac p-5" data-testid="payment-schedule">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Payment schedule / deposits</span>
          <button onClick={addMilestone} data-testid="add-milestone-btn" className="inline-flex items-center gap-1 text-xs text-cyan-400"><Plus className="h-3 w-3" /> Add milestone</button>
        </div>
        {schedule.length === 0 && <p className="text-xs text-slate-500">No milestones. Add e.g. "Equipment + tax upfront" (fixed) or "50% deposit" (percent). Customers pay each via Stripe.</p>}
        {schedule.map((m, i) => (
          <div key={i} className="mb-2 flex flex-wrap items-center gap-2" data-testid={`milestone-${i}`}>
            <input value={m.label} onChange={(e) => setMilestone(i, { label: e.target.value })} data-testid={`milestone-label-${i}`}
              className="flex-1 rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-sm text-white" />
            <select value={m.mode} onChange={(e) => setMilestone(i, { mode: e.target.value })} data-testid={`milestone-mode-${i}`}
              className="rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-sm text-white">
              <option value="percent">%</option><option value="fixed">$ fixed</option>
            </select>
            <input type="number" value={m.value} onChange={(e) => setMilestone(i, { value: e.target.value })} data-testid={`milestone-value-${i}`}
              className="w-20 rounded border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-right text-sm text-white" />
            <span className="w-24 text-right font-mono-tac text-sm text-slate-300">${mAmount(m).toFixed(2)}</span>
            {m.status === "paid" ? <span className="text-xs text-emerald-400">PAID</span>
              : <button onClick={() => delMilestone(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>

      {/* Notes */}
      <div className="mt-6 card-tac p-5">
        <div className="mb-2 font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Notes to customer</div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="quote-notes"
          className="w-full rounded border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white" />
      </div>

      {/* Actions */}
      <div className="mt-6 card-tac p-5">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => save()} disabled={busy} data-testid="save-quote-btn" className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm disabled:opacity-50"><Save className="h-4 w-4" /> Save</button>
          <button onClick={downloadPdf} data-testid="download-quote-pdf-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-white hover:border-cyan-500"><Download className="h-4 w-4 text-cyan-400" /> Download PDF</button>
          <div className="flex flex-1 items-center gap-2">
            <input placeholder="customer@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quote-email-input"
              className="min-w-[180px] flex-1 rounded-full border border-slate-700 bg-[#0d1420] px-4 py-2.5 text-sm text-white" />
            <button onClick={sendQuote} disabled={busy} data-testid="send-quote-btn" className="inline-flex items-center gap-2 rounded-full border border-[#2E7BE4] px-5 py-2.5 text-sm text-[#2E7BE4] hover:bg-[#2E7BE4]/10 disabled:opacity-50"><Send className="h-4 w-4" /> Send</button>
          </div>
        </div>
        {publicUrl && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-[#0d1420] p-3" data-testid="public-link-row">
            <Link2 className="h-4 w-4 text-[#2E7BE4]" />
            <a href={publicUrl} target="_blank" rel="noreferrer" className="flex-1 truncate text-xs text-cyan-300">{publicUrl}</a>
            <button onClick={copyLink} data-testid="copy-link-btn" className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-cyan-500"><Copy className="h-3 w-3" /> Copy</button>
          </div>
        )}
        {q.status === "accepted" && (
          <div className="mt-4 rounded-xl border border-emerald-600/40 bg-emerald-950/10 p-3 text-sm" data-testid="accepted-panel">
            <div className="flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Accepted{q.signer_name ? ` by ${q.signer_name}` : ""}{q.accepted_at ? ` · ${String(q.accepted_at).slice(0, 10)}` : ""}</div>
            {q.accounting && (q.accounting.qbo_ref || q.accounting.xero_ref) && (
              <p className="mt-1 font-mono-tac text-xs text-slate-400">Invoiced: {[q.accounting.qbo_ref, q.accounting.xero_ref].filter(Boolean).join(" · ")}</p>
            )}
          </div>
        )}
        {canOffice && (
          <button onClick={pushAccounting} disabled={busy} data-testid="push-accounting-btn" className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-white hover:border-emerald-500 disabled:opacity-50"><PlugZap className="h-4 w-4 text-emerald-400" /> Push invoice to QuickBooks / Xero</button>
        )}
      </div>
    </div>
  );
}
